import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { Trans } from "react-i18next"
import theme from "lib/material-ui/theme"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { FILTER_NOTIFICATIONS, NAVIGATE_TO_OTHER_USER_PROFILE_PAGE } from "constants/events"
import { FOLLOW_REQUEST, NEW_FOLLOWER, NOTIFICATIONS_MAP, UPCOMING_BIRTHDAY } from "constants/notifications"
import { formatRelativeDate } from "utils/dateUtils"
import { NotificationType, WishamNotification } from "types/notification.types"
import { isActorNotification, messageHandlers, notificationClickHandlers } from "utils/notifications"
import EventIcon from "components/atoms/_icons/EventIcon"
import PremiumLogo from "components/atoms/_icons/PremiumLogo"
import Avatar from "components/atoms/Avatar"
import NotificationMessage from "components/molecules/Notification/NotificationMessage"
import FollowRequestButtons from "components/molecules/Notification/FollowRequestButtons"
import FollowButton from "components/molecules/FollowButton"

type NotificationProps = {
  notification: WishamNotification;
}

export default function Notification(props: NotificationProps) {
  const { notification } = props

  const [message, setMessage] = useState<string>("")
  const [followAccepted, setFollowAccepted] = useState<boolean>(false)
  const [followStatus, setFollowStatus] = useState<FollowStatus | null>(null)

  const dispatch = useDispatch()

  useEffect(() => {
    setMessage(NOTIFICATIONS_MAP[notification.type]?.message)
  }, [notification.type])

  function handleNotificationClicked(notificationType: NotificationType) {
    const eventDetail = notificationClickHandlers[notificationType]?.(notification, dispatch) ?? (isActorNotification(notification) ?
      { id: notification.userId, handle: notification.handle } :
      { id: notification.owner.id })
    document.dispatchEvent(new CustomEvent(NOTIFICATIONS_MAP[notification.type].onClickEvent, { detail: eventDetail }))
  }

  function handleUserAvatarClicked() {
    const eventDetail = (isActorNotification(notification) ?
      { id: notification.userId, handle: notification.handle } :
      { id: notification.owner.id })
    document.dispatchEvent(new CustomEvent(NAVIGATE_TO_OTHER_USER_PROFILE_PAGE, { detail: eventDetail }))
  }

  function handleFollowAccepted(notificationId: number, followerId: number, status: FollowStatus) {
    setMessage(NOTIFICATIONS_MAP[NEW_FOLLOWER].message)
    setFollowAccepted(true)
    setFollowStatus(status)
    document.dispatchEvent(new CustomEvent(FILTER_NOTIFICATIONS, { detail: { notificationId, followerId } }))
  }

  function handleFollowRejected(notificationId: number, followerId: number) {
    document.dispatchEvent(new CustomEvent(FILTER_NOTIFICATIONS, {
      detail: {
        notificationId,
        followerId,
        type: "ALL"
      }
    }))
  }

  function getMessageDynamicValue(notificationType: NotificationType) {
    return messageHandlers[notificationType]?.(notification) ?? (isActorNotification(notification) ? { handle: notification.handle } : {})
  }

  function getNotificationIcon() {
    switch (notification.type) {
      case "PREMIUM_EXPIRED":
      case "PREMIUM_EXPIRING":
        return <Stack justifyContent="center" alignItems="center" width="40px" height="40px">
          <PremiumLogo/>
        </Stack>
      default:
        return null
    }
  }

  return (
    <NotificationContainer>
      {NOTIFICATIONS_MAP[notification.type] && <Wrapper sx={{ width: "100%" }}>
          <Wrapper>
            {isActorNotification(notification) ? <Avatar
              handle={notification.handle}
              width={"40px"}
              imageSrc={notification.avatar}
              badge={notification.type === UPCOMING_BIRTHDAY ? <EventIcon severity="primary"/> : null}
              onClick={handleUserAvatarClicked}
            /> : getNotificationIcon()}
              <NotificationMessage>
                  <span
                      className="farsi-digits"
                      onClick={() => handleNotificationClicked(notification.type)}
                      style={{ cursor: "pointer" }}
                  >
                    <Trans
                        i18nKey={message}
                        values={getMessageDynamicValue(notification.type)}
                        components={{
                          strong: <strong style={{
                            color: theme.palette.gray["15"],
                            fontWeight: 700
                          }}/>,
                          span: <span/>
                        }}
                    />
                  </span>
                  <NotificationTime>
                    {formatRelativeDate(notification.date)}
                  </NotificationTime>
              </NotificationMessage>
          </Wrapper>
        {(isActorNotification(notification) && notification.type === FOLLOW_REQUEST && !followAccepted) && <FollowRequestButtons
            id={notification.id}
            followerId={notification.userId}
            onAccept={handleFollowAccepted}
            onReject={handleFollowRejected}
        />}
        {isActorNotification(notification) && (notification.type === FOLLOW_REQUEST && followAccepted && followStatus) && <FollowButton
            userId={notification.userId}
            handle={notification.handle}
            status={followStatus}
        />}
      </Wrapper>}
    </NotificationContainer>
  )
}

const NotificationContainer = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "4px",
  margin: "8px 0",

  "& .avatar": {
    width: "40px",
    height: "40px",
    marginRight: 0
  }
}))

const Wrapper = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "8px"
}))

const NotificationTime = styled("span")(({ theme }) => ({
  fontSize: "0.9rem",
  color: theme.palette.gray["12"],
  fontWeight: 400,
  marginLeft: "4px",
  whiteSpace: "nowrap",
  fontFamily: "VazirmatnFD, serif !important"
}))
